// MIRROR (with two marked LANDING-ONLY divergences: continuous `t`, layout-size resize) of apps/web/src/components/meeting/presenters/useCanvasLoop.ts — keep in sync.
// apps/landing cannot import from apps/web; the body below is byte-identical to the source.
/**
 * Canvas plumbing shared by the animated presenters: DPR-correct sizing, a
 * resize observer, one rAF loop, and reduced-motion handling.
 *
 * Under prefers-reduced-motion the loop does not run. The draw callback is
 * still invoked on state change and on resize, with `t` frozen at 0, so every
 * state stays visually distinct — it just holds still.
 */
import { useEffect, useRef } from 'react';

export interface DrawArgs {
  ctx: CanvasRenderingContext2D;
  /** CSS pixels, not device pixels — the context is already scaled. */
  w: number;
  h: number;
  /** Seconds since the loop started. 0 when motion is reduced. */
  t: number;
  reduced: boolean;
}

export function useCanvasLoop(draw: (a: DrawArgs) => void, deps: unknown[]) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const startRef = useRef(performance.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let w = 0;
    let h = 0;
    let raf = 0;
    // LANDING-ONLY divergence: `t` is continuous across restarts (deps changes), so breathing and
    // blink phases never jump when the state changes. The source restarts at 0.
    const start = startRef.current;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      // LANDING-ONLY divergence: layout size, not the transformed box — the landing squashes and
      // scales the character's body in CSS, and a resize measured mid-squash would draw it small.
      const rect = { width: canvas.offsetWidth || canvas.getBoundingClientRect().width, height: canvas.offsetHeight || canvas.getBoundingClientRect().height };
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const paint = () => {
      ctx.clearRect(0, 0, w, h);
      drawRef.current({ ctx, w, h, t: reduced ? 0 : (performance.now() - start) / 1000, reduced });
    };

    resize();
    paint();

    const ro = new ResizeObserver(() => { resize(); paint(); });
    ro.observe(canvas);

    if (!reduced) {
      const loop = () => { paint(); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return canvasRef;
}
